import Draggable16 from '@carbon/icons-react/es/draggable/16';
import Export16 from '@carbon/icons-react/es/export/16';
import Filter16 from '@carbon/icons-react/es/filter/16';
import { Tooltip, Typography } from 'antd';
import { ParagraphProps } from 'antd/es/typography/Paragraph';
import React, { FC, ReactElement, memo, useCallback } from 'react';
import styled from 'styled-components';
import { SimpleSearchFilter } from 'api/dashboard/widget';
import { Project } from 'api/project';
import { Button } from 'components/atoms/button';
import { IconWrapper } from 'components/atoms/icon-shell/icon-shell';
import { Modal } from 'components/atoms/modal';
import { MoreActionsDropdown } from 'components/atoms/more-actions';
import { TableSkeleton } from 'components/atoms/table';
import { Filter } from 'components/molecules/filters/types';
import { GroupedFilter } from 'components/organisms/tasks/grouped-filter-popover';
import { HelperText01, ProductiveHeading05 } from 'components/styles';
import { useTranslate } from 'hooks/use-translation';
import { DashBoardProjectList } from 'routes/projects/list-v2/dashboards-project-list';
import { FilterAsText } from '../filter-as-text';
import { DrilldownTitleWrapper, ListWrapper } from './styles';
import { useDashboardExports } from './utils';
import { useChartDndContext } from './widget-dnd';

const InfoIcon = styled(Filter16)`
  color: var(--gray-80);
  flex-shrink: 0;
`;

export const BaseTile = styled.div`
  padding: var(--spacing-03) var(--spacing-03) var(--spacing-05)
    var(--spacing-05);
  background-color: var(--white-100);
  height: 96px;
  box-shadow: var(--shadow-02);
  .more-actions {
    opacity: 0;
  }
  &:hover .more-actions {
    opacity: 1;
  }
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-03);
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  max-width: calc(100% - 32px);
  gap: var(--spacing-03);
  flex-shrink: 0;
  height: var(--spacing-07);
`;

const Title = styled(Typography.Paragraph)<ParagraphProps>`
  ${HelperText01};
  margin: 0;
`;

const ModalSubtitle = styled.div`
  ${ProductiveHeading05};
`;

const ModalTitle = styled.div``;

const Dnd = styled.span`
  &:hover {
    cursor: grab;
  }
`;

interface NumberWidgetCardProps {
  widgetId?: number;
  filter: GroupedFilter;
  dashboardFilter?: Filter;
  widgetName: string;
  widgetMenu?: ReactElement;
  preview: boolean;
  content: JSX.Element | null;
  loading?: boolean;
  listData?: Project[];
  setData?: React.Dispatch<React.SetStateAction<Project[]>>;
  modalContent?: JSX.Element | null;
  closeModal?: () => void;
  isModalVisible?: boolean;
  fieldNames?: string[];
  dashboardSimpleFilter?: SimpleSearchFilter;
}

const ProjectList = memo(DashBoardProjectList);

const ModalHeader = ({
  widgetId,
  widgetName,
  modalSubContent,
  dashboardSimpleFilter,
}) => {
  const translate = useTranslate();
  const { exportFn, isExportInProgress } = useDashboardExports(
    widgetId as number,
  );
  const exportData = useCallback(() => {
    exportFn({
      dashboardFilter: dashboardSimpleFilter,
    });
  }, [exportFn, dashboardSimpleFilter]);

  return (
    <div>
      <ModalTitle>{widgetName}</ModalTitle>
      <DrilldownTitleWrapper>
        <ModalSubtitle>{modalSubContent}</ModalSubtitle>
        <Button
          icon={<Export16 />}
          iconPlacement="right"
          onClick={exportData}
          loading={isExportInProgress}
          type="ghost"
        >
          {translate('export')}
        </Button>
      </DrilldownTitleWrapper>
    </div>
  );
};

export const NumberWidgetCard: FC<NumberWidgetCardProps> = (
  props: NumberWidgetCardProps,
) => {
  const {
    filter,
    widgetName,
    widgetMenu,
    preview = false,
    content,
    dashboardFilter,
    listData = [],
    fieldNames = [],
    ...rest
  } = props;

  const { listeners, attributes } = useChartDndContext();

  return (
    <BaseTile>
      <Row>
        <TitleWrapper>
          {listeners && attributes ? (
            <Dnd {...listeners} {...attributes}>
              <IconWrapper>
                <Draggable16 />
              </IconWrapper>
            </Dnd>
          ) : null}
          <Title
            title={widgetName}
            ellipsis={{
              rows: 1,
            }}
          >
            {widgetName}
          </Title>
          {filter.groupCriteria.length > 0 ||
          dashboardFilter?.criteria?.length ? (
            <Tooltip
              overlayStyle={{ maxWidth: 'unset' }}
              title={
                <FilterAsText
                  filter={filter}
                  dashboardFilter={dashboardFilter}
                />
              }
            >
              <InfoIcon />
            </Tooltip>
          ) : null}
        </TitleWrapper>
        {widgetMenu && (
          <div className="more-actions">
            <MoreActionsDropdown menu={widgetMenu} />
          </div>
        )}
      </Row>
      {content}
      {!preview && (
        <Modal
          visible={rest.isModalVisible}
          confirmLoading={false}
          onCancel={() => {
            rest?.closeModal?.();
            rest?.setData?.([]);
          }}
          footer={null}
          title={
            <ModalHeader
              widgetId={rest.widgetId}
              widgetName={widgetName}
              modalSubContent={rest.modalContent}
              dashboardSimpleFilter={rest.dashboardSimpleFilter}
            />
          }
          destroyOnClose
          maskClosable={false}
          keyboard={false}
          width="80%"
        >
          {rest.loading ? (
            <TableSkeleton rowCount={9} />
          ) : (
            <ListWrapper>
              <ProjectList data={listData} fieldNames={fieldNames} />
            </ListWrapper>
          )}
        </Modal>
      )}
    </BaseTile>
  );
};
